import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../components/DataTable'
import { adminFinance } from '../api/finance'
import type { TimelineEvent, TimelineSubscription, UserTimeline } from '../api/financeTypes'
import { useEmailParam } from '../lib/useEmailParam'
import { DASH, decimalSortKey, formatCount, formatMoney, isNegative } from '../lib/money'
import { formatDate } from '../lib/format'
import { SoonButton } from './actions'
import { creditPage, subscriptionPage } from './links'
import { useFinance } from './financeContext'
import { SUB_STATUS, isBalancePaid, shortStamp } from './labels'
import { AdminOpsTable, StripeLogTable } from './logTables'
import { resultTone } from './logFormat'
import { Card, Chip, FinanceQuery, HelpLabel, MoneyLines, PageHead, Segmented, type Tone } from './ui'
import { reasonFor } from '../lib/financeErrors'

function UserSearch() {
  const [email, setEmail] = useState('')
  const navigate = useNavigate()
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const v = email.trim()
    if (v) navigate(`/admin/finance/users?email=${encodeURIComponent(v)}`)
  }
  return (
    <>
      <PageHead title="Felhasználó-kereső" sub="Add meg egy ügyfél e-mail címét a pénzügyi 360° nézethez" />
      <Card>
        <form className="af-search-form" onSubmit={submit}>
          <label>
            E-mail
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ugyfel@example.com" />
          </label>
          <button type="submit" className="af-btn af-btn--primary">
            Megnyitás
          </button>
        </form>
      </Card>
    </>
  )
}

type Tab = 'all' | 'charge' | 'credit' | 'sub' | 'admin'
const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: 'Mind' },
  { key: 'charge', label: 'Terhelések' },
  { key: 'credit', label: 'Kredit' },
  { key: 'sub', label: 'Előfizetés' },
  { key: 'admin', label: 'Admin' },
]
const tabOf = (e: TimelineEvent): Tab => (e.type === 'charge_attempt' || e.type === 'payment' ? 'charge' : e.type === 'credit' ? 'credit' : e.type === 'subscription' ? 'sub' : e.type === 'admin' ? 'admin' : 'all')

const FAILED = /declin|refus|fail/i

function Timeline({ events }: { events: TimelineEvent[] }) {
  const [tab, setTab] = useState<Tab>('all')
  const rows = tab === 'all' ? events : events.filter((e) => tabOf(e) === tab)
  return (
    <Card className="af-stack">
      <div className="af-card__head">
        <HelpLabel helpKey="timeline" as="h2">
          Pénzügyi idővonal
        </HelpLabel>
        <Segmented label="Idővonal szűrése" options={TABS} value={tab} onChange={setTab} />
      </div>
      {rows.length === 0 ? (
        <p className="portal-text-muted">Nincs esemény ebben a szűrésben.</p>
      ) : (
        <ol className="af-timeline">
          {rows.map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <span className="af-num af-small af-muted">{shortStamp(e.at)}</span>
              <span className="af-stack af-stack--tight">
                <span>
                  <strong>{e.title}</strong> {isBalancePaid(e.title) && <Chip tone="info">Egyenlegből fizetve</Chip>} {e.status && <Chip tone={resultTone(e.status) as Tone}>{e.status}</Chip>}
                </span>
                {e.detail && <span className="af-sub">{e.detail}</span>}
              </span>
              <span className={`af-num${e.amount && isNegative(e.amount) ? ' af-down' : ''}`}>{e.amount ? formatMoney(e.amount, e.currency) : DASH}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}

function Profile({ d }: { d: UserTimeline }) {
  const fin = useFinance()
  const linkProject = fin.projectId ?? d.projects[0]?.id
  const overdue = d.subscriptions.filter((s) => s.status === 3)
  const activeSubs = d.subscriptions.filter((s) => s.status === 1)
  const [now] = useState(() => Date.now())
  const cutoff = now - 90 * 86_400_000
  const failed90 = d.events.filter((e) => e.type === 'charge_attempt' && FAILED.test(e.status ?? '') && new Date(e.at).getTime() >= cutoff).length
  const projectName = (id: string) => d.projects.find((p) => p.id === id)?.name ?? id

  const subColumns: Column<TimelineSubscription>[] = [
    { key: 'plan', label: 'Csomag / termék', sortValue: (s) => s.plan, format: (_v, s) => <strong>{s.plan}</strong> },
    { key: 'project', label: 'Projekt', sortValue: (s) => projectName(s.project_id), format: (_v, s) => projectName(s.project_id) },
    { key: 'fee', label: 'Díj', numeric: true, sortValue: (s) => decimalSortKey(s.monthly_fee), format: (_v, s) => (s.monthly_fee ? formatMoney(s.monthly_fee, s.currency) : DASH) },
    {
      key: 'status',
      label: 'Állapot',
      sortValue: (s) => s.status,
      format: (_v, s) => {
        const st = SUB_STATUS[s.status] ?? { label: s.status_label, tone: 'mute' as const }
        return <Chip tone={st.tone}>{st.label}</Chip>
      },
    },
    { key: 'exp', label: 'Lejár', sortValue: (s) => s.expires_at ?? null, format: (_v, s) => (s.expires_at ? formatDate(s.expires_at) : DASH) },
    { key: 'ok', label: 'Utolsó siker', sortValue: (s) => s.last_success_at ?? null, format: (_v, s) => (s.last_success_at ? formatDate(s.last_success_at) : DASH) },
    { key: 'fail', label: 'Utolsó hiba', sortValue: (s) => s.last_fail_at ?? null, format: (_v, s) => (s.last_fail_at ? formatDate(s.last_fail_at) : DASH) },
    { key: 'unpaid', label: 'Tartozás', numeric: true, sortValue: (s) => decimalSortKey(s.unpaid_fee), format: (_v, s) => (s.unpaid_fee ? formatMoney(s.unpaid_fee, s.currency) : DASH) },
  ]

  return (
    <>
      <p className="af-crumbs">
        <Link to="/admin/finance/problems">Figyelmet igényel</Link> / {d.email}
      </p>
      <Card className="af-profile">
        <div className="af-stack">
          <h1>{d.email}</h1>
          <div className="af-sub">
            {d.projects.length === 0 ? 'Nincs projekt-tagság' : d.projects.map((p) => `${p.name} (${p.roles.join(', ')})`).join(', ')}
            {d.billing_account_ids.length > 0 && (
              <>
                {' · számla-azonosító '}
                <span className="af-num">{d.billing_account_ids.map((b) => `…${b.slice(-4)}`).join(', ')}</span>
              </>
            )}
          </div>
          <div className="af-chips">
            {overdue.map((s) => (
              <Chip key={s.id} tone="warn">
                Tartozik · {s.unpaid_fee ? formatMoney(s.unpaid_fee, s.currency) : DASH}
              </Chip>
            ))}
            {d.payment_methods.map((m, i) => (
              <Chip key={i} tone="mute">
                {m.brand ? m.brand.toUpperCase() : 'Kártya'} •••• {m.last4 ?? '—'}
                {m.expires_at ? ` · lejár ${m.expires_at.replace('-', '/')}` : ''}
              </Chip>
            ))}
            {d.payment_methods.length === 0 && <Chip tone="mute">Nincs mentett kártya</Chip>}
          </div>
        </div>
        <div className="af-actions">
          <SoonButton primary>Terhelés újrapróbálása</SoonButton>
          {linkProject && (
            <>
              <Link className="af-btn" to={creditPage(linkProject, d.email)}>
                Kredit jóváírás / levonás
              </Link>
              <Link className="af-btn" to={subscriptionPage(linkProject, d.email)}>
                Előfizetés állapota
              </Link>
            </>
          )}
          <SoonButton>Ügyfél értesítése</SoonButton>
        </div>
      </Card>

      <section className="af-kpis af-kpis--4">
        <Card>
          <HelpLabel helpKey="lifetime" className="af-lbl">
            Életidő-bevétel
          </HelpLabel>
          <div className="af-kv af-num af-kv--sm">
            <MoneyLines list={d.lifetime_revenue} reason={reasonFor(d.errors, 'lifetime_revenue')} />
          </div>
          <div className="af-sub">{d.lifetime_revenue?.map((m) => `${formatCount(m.count)} sikeres terhelés`).join(' · ') ?? DASH}</div>
        </Card>
        <Card>
          <div className="af-lbl">Aktív előfizetés</div>
          <div className="af-kv af-num">{formatCount(activeSubs.length)}</div>
          <div className="af-sub">{activeSubs.map((s) => `${s.plan} · ${s.monthly_fee ? formatMoney(s.monthly_fee, s.currency) : DASH} / hó`).join(' · ') || DASH}</div>
        </Card>
        <Card>
          <HelpLabel helpKey="balances" className="af-lbl">
            Kredit-egyenlegek
          </HelpLabel>
          <div className="af-kv af-num af-kv--sm af-money-lines">
            {d.balances.length === 0 ? DASH : d.balances.map((b) => <span key={b.currency} className={isNegative(b.amount) ? 'af-down' : undefined}>{formatMoney(b.amount, b.currency)}</span>)}
          </div>
        </Card>
        <Card>
          <div className="af-lbl">Sikertelen terhelés (90 nap)</div>
          <div className={`af-kv af-num${failed90 > 0 ? ' af-kv--warn' : ''}`}>{formatCount(failed90)} db</div>
          <div className="af-sub">az idővonal betöltött eseményei alapján</div>
        </Card>
      </section>

      <Card className="af-stack">
        <h2>Előfizetések és vásárlások</h2>
        {d.subscriptions.length === 0 ? (
          <p className="portal-text-muted">Nincs előfizetés.</p>
        ) : (
          <div className="portal-table-scroll">
            <Table columns={subColumns} rows={d.subscriptions} rowKey={(s) => s.id} initialSort={{ key: 'exp', dir: 'desc' }} />
          </div>
        )}
      </Card>

      <section className="af-grid">
        <Timeline events={d.events} />
        <div className="af-stack">
          <Card className="af-stack">
            <div>
              <HelpLabel helpKey="stripeLog" as="h2">
                Stripe-napló
              </HelpLabel>
              <p className="af-sub">Minden Stripe-hívás egy sor · kártyaadat nincs tárolva</p>
            </div>
            {d.stripe_log.length === 0 ? <p className="portal-text-muted">Nincs Stripe-hívás.</p> : <StripeLogTable rows={d.stripe_log} />}
            <Link to={`/admin/finance/stripe-log?email=${encodeURIComponent(d.email)}`}>Teljes Stripe-napló →</Link>
          </Card>
          <Card className="af-stack">
            <HelpLabel helpKey="adminOps" as="h2">
              Admin-műveletek ezen a felhasználón
            </HelpLabel>
            {d.admin_operations.length === 0 ? <p className="portal-text-muted">Nincs admin-művelet.</p> : <AdminOpsTable rows={d.admin_operations} />}
          </Card>
        </div>
      </section>
    </>
  )
}

function UserDetail({ email }: { email: string }) {
  const fin = useFinance()
  const q = useApiQuery(() => adminFinance.userTimeline(fin.projectId, email), [fin.projectId, email])
  return <FinanceQuery q={q}>{(d) => <Profile d={d} />}</FinanceQuery>
}

export function UserPage() {
  const email = useEmailParam()
  return email ? <UserDetail email={email} /> : <UserSearch />
}
